<template>
    <card-wrapper :title="Lang.get('employerdashboard::pages/employee/details.title', {fullname: employee.fullname})"
                  :subtitle="Lang.get('employerdashboard::pages/employee/details.subtitle')">
        <template v-slot:nav aria-label="breadcrumb">
            <ol class="breadcrumb font-poppins font-weight-light font-size-14 color-blue px-0 bg-transparent">
                <li class="breadcrumb-item">
                    <a
                        :href="route('nl.employerdashboard.employees.overview', $page.props.auth.user.selected_company.uuid)"
                        class="font-poppins font-weight-semibold color-blue">
                        {{ Lang.get('employerdashboard::pages/employee/breadcrumb.overview') }}</a>
                </li>
                <li class="breadcrumb-item">
                    <a
                        :href="route('nl.employerdashboard.employee.profile', {company_as_uuid: $page.props.auth.user.selected_company.uuid, employee_uuid: employee.uuid})"
                        class="font-poppins font-weight-semibold color-blue">
                        {{ Lang.get('employerdashboard::pages/employee/breadcrumb.profile') }}</a>
                </li>
                <li class="breadcrumb-item color-blue" aria-current="page">
                    {{ Lang.get('employerdashboard::pages/employee/breadcrumb.details') }}
                </li>
            </ol>
        </template>

        <div class="row mt-5" v-if="employee.is_disabled_from_participation">
            <div class="col-8 mb-4">
                <div class="alert alert-warning">
                    <i class="fa fa-exclamation-circle pr-2"></i> Deze medewerker is geanonimiseerd
                    <p class="pt-2">
                        <strong>{{ Lang.get('employerdashboard::pages/employee/profile.explanation') }}:</strong><br/>
                        {{
                            Lang.get(
                                'employerdashboard::pages/employee/profile.is_disabled_from_participation',
                                {
                                    reason: employee.disabled_from_participation_reason,
                                    reference: employee.disabled_from_participation_reference,
                                }
                            )
                        }}
                    </p>
                </div>
            </div>
        </div>

        <div class="col-12 col-md-8">
            <flash-messages></flash-messages>
        </div>

        <card-layout size="large" v-if="!employee.is_disabled_from_participation">
            <div class="form-row m-3">
                <div class="form-group col-12 col-md-6 col-lg-4">
                    <text-input
                        v-model="form.first_name"
                        :label="Lang.get('employerdashboard::shared.firstname')"
                        :placeholder="Lang.get('employerdashboard::shared.placeholder.firstname')"
                        :readOnly="true"
                        disabled="disabled"
                    />
                </div>
                <div class="form-group col-12 col-md-6 col-lg-4">
                    <text-input
                        v-model="form.middle_name"
                        :label="Lang.get('employerdashboard::shared.middlename')"
                        :placeholder="Lang.get('employerdashboard::shared.placeholder.middlename_unknown')"
                        :readOnly="true"
                        disabled="disabled"
                    />
                </div>
                <div class="form-group col-12 col-md-6 col-lg-4">
                    <text-input
                        v-model="form.last_name"
                        :label="Lang.get('employerdashboard::shared.lastname')"
                        :placeholder="Lang.get('employerdashboard::shared.placeholder.lastname')"
                        :readOnly="true"
                        disabled="disabled"
                    />
                </div>
            </div>

            <div class="form-row m-3" v-if="hasFormKey('date_of_birth') || hasFormKey('birthplace')">
                <div class="form-group col-12 col-md-6 col-lg-4">
                    <text-input
                        v-model="form.initials"
                        :label="Lang.get('employerdashboard::shared.initials')"
                        :placeholder="Lang.get('employerdashboard::shared.placeholder.initials')"
                        :readOnly="true"
                        disabled="disabled"
                    />
                </div>
                <div class="form-group col-12 col-md-6 col-lg-4">
                    <text-input
                        v-if="hasFormKey('date_of_birth')"
                        type="date"
                        v-model="form.date_of_birth"
                        :label="Lang.get('employerdashboard::shared.date_of_birth')"
                        :placeholder="Lang.get('employerdashboard::shared.placeholder.date_of_birth')"
                        :readOnly="true"
                        disabled="disabled"
                    />
                </div>
                <div class="form-group col-12 col-md-6 col-lg-4">
                    <text-input
                        v-if="hasFormKey('birthplace')"
                        v-model="form.birthplace"
                        :label="Lang.get('employerdashboard::shared.birth_place')"
                        :placeholder="Lang.get('employerdashboard::shared.placeholder.birth_place_unknown')"
                        :readOnly="true"
                        disabled="disabled"
                    />
                </div>
            </div>

            <template v-if="hasAddressFields">
                <div class="form-row m-3 border-bottom"></div>
                <div class="form-row m-3">
                    <div class="form-group col-12 col-md-6 col-lg-4">
                        <text-input
                            v-model="form.street"
                            :label="Lang.get('employerdashboard::shared.street')"
                            :placeholder="Lang.get('employerdashboard::shared.placeholder.street')"
                            :readOnly="true"
                            disabled="disabled"
                        />
                    </div>
                    <div class="form-group col-12 col-md-6 col-lg-4">
                        <text-input
                            v-model="form.house_number"
                            :label="Lang.get('employerdashboard::shared.house_number')"
                            :placeholder="Lang.get('employerdashboard::shared.placeholder.house_number')"
                            :readOnly="true"
                            disabled="disabled"
                        />
                    </div>
                    <div class="form-group col-12 col-md-6 col-lg-4">
                        <text-input
                            v-model="form.house_number_addition"
                            :label="Lang.get('employerdashboard::shared.house_number_addition')"
                            :placeholder="Lang.get('employerdashboard::shared.placeholder.house_number_addition_unknown')"
                            :readonly="true"
                            disabled="disabled"
                        />
                    </div>
                </div>

                <div class="form-row m-3">
                    <div class="form-group col-12 col-md-6 col-lg-4">
                        <text-input
                            v-model="form.zip_code"
                            :label="Lang.get('employerdashboard::shared.zipcode')"
                            :placeholder="Lang.get('employerdashboard::shared.placeholder.zipcode')"
                            :readOnly="true"
                            disabled="disabled"
                        />
                    </div>
                    <div class="form-group col-12 col-md-6 col-lg-4">
                        <text-input
                            v-model="form.city"
                            :label="Lang.get('employerdashboard::shared.city')"
                            :placeholder="Lang.get('employerdashboard::shared.placeholder.city')"
                            :readOnly="true"
                            disabled="disabled"
                        />
                    </div>
                    <div class="form-group col-12 col-md-6 col-lg-4">
                        <select-input
                            v-model="form.country_code"
                            :label="Lang.get('employerdashboard::shared.country')"
                            :placeholder="Lang.get('employerdashboard::shared.placeholder.country')"
                            :readOnly="true"
                            disabled="disabled">
                            <option :value="null">{{ Lang.get('employerdashboard::shared.choose') }}</option>
                            <option value="NL" v-if="!form.country">{{ preferred_country }}</option>
                            <option v-for="(countryName, countryCode) in countries" :value="countryCode">
                                {{ countryName }}
                            </option>
                        </select-input>
                    </div>
                </div>
            </template>

            <div class="form-row m-3 border-bottom"></div>

            <div class="form-row m-3">
                <div class="form-group col-12 col-md-6 col-lg-4">
                    <text-input
                        v-model="form.email_personal"
                        :label="Lang.get('employerdashboard::shared.email_personal')"
                        :placeholder="Lang.get('employerdashboard::shared.placeholder.email_personal')"
                        :readOnly="true"
                        disabled="disabled"
                    />
                </div>
                <div class="form-group col-12 col-md-6 col-lg-4">
                    <text-input
                        v-model="form.email_business"
                        :label="Lang.get('employerdashboard::shared.email_business')"
                        :placeholder="Lang.get('employerdashboard::shared.placeholder.email_business')"
                        :readOnly="true"
                        disabled="disabled"
                    />
                </div>
                <div class="form-group col-12 col-md-6 col-lg-4">
                    <text-input
                        v-model="form.email"
                        :label="Lang.get('employerdashboard::shared.email')"
                        :placeholder="Lang.get('employerdashboard::shared.placeholder.email')"
                        :readOnly="true"
                        disabled="disabled"
                    />
                </div>
            </div>

            <div class="form-row m-3">
                <div class="form-group col-md-4">
                    <text-input
                        v-model="form.phonenumber"
                        :label="Lang.get('employerdashboard::shared.phone')"
                        :placeholder="Lang.get('employerdashboard::shared.placeholder.phone')"
                        :readOnly="true"
                        disabled="disabled"
                    />
                </div>
            </div>

            <div class="form-row m-3 border-bottom"></div>

            <div class="form-row m-3">
                <div class="form-group col-12 col-md-6 col-lg-4">
                    <text-input
                        v-model="form.employee_number"
                        :label="Lang.get('employerdashboard::shared.employee_number')"
                        :placeholder="Lang.get('employerdashboard::shared.placeholder.employee_number')"
                        :readOnly="true"
                        disabled="disabled"
                    />
                </div>
                <div class="form-group col-12 col-md-6 col-lg-4">
                    <text-input
                        v-model="form.function"
                        :label="Lang.get('employerdashboard::shared.function')"
                        :placeholder="Lang.get('employerdashboard::shared.placeholder.function')"
                        :readOnly="true"
                        disabled="disabled"
                    />
                </div>
            </div>

            <div class="form-row m-3">
                <div class="form-group col-12 col-md-6 col-lg-4">
                    <text-input
                        type="date"
                        v-model="form.hired_at"
                        :label="Lang.get('employerdashboard::shared.hired_at')"
                        :placeholder="Lang.get('employerdashboard::shared.placeholder.hired_at')"
                        :readOnly="true"
                        disabled="disabled"
                    />
                </div>
                <div class="form-group col-12 col-md-6 col-lg-4">
                    <text-input
                        :type="form.fired_at ? 'date' : 'text'"
                        v-model="form.fired_at"
                        :label="Lang.get('employerdashboard::shared.fired_at')"
                        :placeholder="Lang.get('employerdashboard::shared.placeholder.fired_at_unknown')"
                        :readOnly="true"
                        disabled="disabled"
                    />
                </div>
                <div class="form-group col-12 col-md-6 col-lg-4">
                    <select-input
                        v-model="form.payroll_tax_credit"
                        :label="Lang.get('employerdashboard::shared.payroll_tax_credit')"
                        :placeholder="Lang.get('employerdashboard::shared.placeholder.payroll_tax_credit')"
                        :readOnly="true"
                        disabled="disabled">
                        <option :value="null">{{ Lang.get('employerdashboard::shared.choose') }}</option>
                        <option v-for="(payrollTaxCredit, payrollTaxCreditCode) in payroll_tax_credit_options"
                                :value="payrollTaxCreditCode">{{ payrollTaxCredit }}
                        </option>
                    </select-input>
                </div>
            </div>

            <div class="form-row m-3">
                <div class="form-group col-12 col-md-6 col-lg-4">
                    <text-input
                        type="number"
                        v-model="form.hours"
                        :label="Lang.get('employerdashboard::shared.hours')"
                        :placeholder="Lang.get('employerdashboard::shared.placeholder.hours')"
                        :readOnly="true"
                        disabled="disabled"
                    />
                </div>
                    <div class="form-group col-12 col-md-6 col-lg-4" v-if="employee.hours_min">
                        <text-input
                            type="number"
                            v-model="form.hours_min"
                            :label="Lang.get('employerdashboard::shared.hours_min')"
                            :placeholder="Lang.get('employerdashboard::shared.placeholder.hours_min')"
                            :readOnly="true"
                            disabled="disabled"
                        />
                    </div>
                    <div class="form-group col-12 col-md-6 col-lg-4" v-if="employee.hours_max">
                        <text-input
                            type="number"
                            v-model="form.hours_max"
                            :label="Lang.get('employerdashboard::shared.hours_max')"
                            :placeholder="Lang.get('employerdashboard::shared.placeholder.hours_max')"
                            :readOnly="true"
                            disabled="disabled"
                        />
                    </div>
            </div>
        </card-layout>

        <div class="col-12 col-md-4 mb-4">
            <div class="row" v-if="$page.props.auth.isCompanyProviderManually">
                <div class="col-12 mb-4">
                    <inertia-link
                        class="btn btn-primary font-poppins font-weight-bold btn-orange border-radius-19 px-4"
                        :href="route('nl.employerdashboard.employee.edit', {
                            'company_as_uuid': this.$page.props.auth.user.selected_company.uuid, 'employee_uuid': employee.uuid})">
                        {{ Lang.get('employerdashboard::shared.edit_employee') }}
                    </inertia-link>
                </div>
            </div>

            <div class="row" v-if="!$page.props.auth.isCompanyProviderManually">
                <div class="col-12 mb-4">
                    <div class="card h-100 border-radius-8 shadow">
                        <div class="card-body">
                            <h4 class="card-title font-poppins font-weight-extrabold color-blue font-size-14">
                                {{ Lang.get('employerdashboard::pages/employee/details.instructions_view_only.title') }}
                            </h4>
                            <div class="card-text color-dark-blue font-size-14">
                                {{
                                    Lang.get('employerdashboard::pages/employee/details.instructions_view_only.description', {'provider': $page.props.auth.companyProvider})
                                }}
                                <br/>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

    </card-wrapper>
</template>

<script>
import CardWrapper from '@/Shared/CardWrapper';
import CardLayout from "@/Shared/CardLayout";
import TextInput from "@/Shared/TextInput";
import SelectInput from "@/Shared/SelectInput";
import FlashMessages from "@/Shared/FlashMessages";

export default {
    metaInfo() {
        return {
            title: Lang.get(
                'employerdashboard::pages/employee/details.title',
                {
                    fullname: this.employee.fullname
                }),
        }
    },
    components: {
        CardWrapper,
        CardLayout,
        SelectInput,
        TextInput,
        FlashMessages,
    },
    props: {
        errors: Object,
        employee: Object,
        payroll_tax_credit_options: Object,
        countries: Object,
        preferred_country: String,
    },
    data() {
        return {
            form: {...this.employee}
        }
    },
    methods: {
        hasFormKey(key) {
            return this.form.hasOwnProperty(key);
        },
    },
    computed: {
        hasAddressFields: function () {
            const addressFields = [
                'street',
                'house_number',
                'house_number_addition',
                'zip_code',
                'city',
                'country_code',
            ];
            return addressFields.every(field => this.hasFormKey(field));
        },
    }
}
</script>
